import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Button } from "../ui/button";
import { formatET } from "../../lib/dateUtils";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useEditConfirm } from "../../context/EditConfirmContext";

export function AffiliateRosterSection() {
  const confirmEdit = useEditConfirm();
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [phoneDraft, setPhoneDraft] = useState({});
  const [emailDraft, setEmailDraft] = useState({});
  const [rateDraft, setRateDraft] = useState({});

  const fetchRoster = async () => {
    try {
      const { data } = await api.get("/admin/affiliate-tracker");
      setRoster(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load the sales rep roster");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRoster(); }, []);

  const update = async (id, payload, label) => {
    const a = roster.find((x) => x.id === id);
    if (!(await confirmEdit(`${a?.display_name || "this affiliate"}'s ${label || "info"}`))) return;
    try {
      await api.patch(`/admin/affiliate-tracker/${id}`, payload);
      toast.success("Affiliate updated");
      fetchRoster();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const managers = roster.filter((a) => a.is_manager_tier);

  const filtered = roster.filter((a) => {
    if (statusFilter !== "all" && a.status !== statusFilter) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (a.affiliate_code || "").toLowerCase().includes(q) || (a.display_name || "").toLowerCase().includes(q) || (a.email || "").toLowerCase().includes(q);
  });

  const activeCount = roster.filter((a) => a.status === "active").length;

  return (
    <div data-testid="affiliate-roster-section">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-2xl text-igc-cream tracking-tight mb-1">All Affiliates</h2>
          <p className="text-igc-muted text-sm" data-testid="affiliate-tracker-active-count">{activeCount} active affiliate{activeCount === 1 ? "" : "s"} — leads assigned, deals closed, and commissions in one place.</p>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-igc-muted" />
          <Input data-testid="affiliate-tracker-search-input" placeholder="Search by name, email, or AFF code..." title="Type any part of a rep's name, email, or affiliate code (AFF001, etc.) to instantly filter the list below." value={search} onChange={(e) => setSearch(e.target.value)} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream pl-8 w-72" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="affiliate-tracker-status-filter" title="Show only Active reps, only Inactive reps, or all of them." className="w-40 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 && (
        <p className="text-igc-muted text-sm" data-testid={loading ? "affiliate-tracker-loading" : "affiliate-tracker-empty-state"}>
          {loading ? "Loading affiliates..." : "No affiliates match this filter. Create affiliate accounts from Admin > Users."}
        </p>
      )}

      <div className="space-y-3">
        {filtered.map((a) => (
          <div key={a.id} className="bg-igc-surface border border-igc-gold/15 p-5" data-testid={`affiliate-tracker-row-${a.id}`}>
            <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
              <div>
                <p className="font-serif text-lg text-igc-cream">
                  <span className="text-igc-goldText" data-testid={`affiliate-tracker-code-${a.id}`}>{a.affiliate_code}</span> — {a.display_name}
                  {a.is_manager_tier && (
                    <span className="ml-2 text-[10px] px-2 py-1 bg-igc-gold/10 text-igc-goldText uppercase tracking-wide border border-igc-gold/30" data-testid={`affiliate-tracker-manager-badge-${a.id}`}>
                      Manager Tier
                    </span>
                  )}
                </p>
                <p className="text-igc-muted text-xs">{a.email || "no email"} — Joined {a.date_joined ? formatET(a.date_joined, "MMM d, yyyy") : "—"}</p>
              </div>
              <button
                data-testid={`affiliate-tracker-status-toggle-${a.id}`}
                onClick={() => update(a.id, { status: a.status === "active" ? "inactive" : "active" }, "status")}
                title="Click to flip this affiliate's status between Active and Inactive. Inactive affiliates keep their history but are treated as no longer working leads."
                className={`text-[10px] px-3 py-1.5 uppercase tracking-wide border ${a.status === "active" ? "bg-igc-success/10 text-igc-success border-igc-success/30" : "bg-igc-muted/10 text-igc-muted border-igc-muted/30"}`}
              >
                {a.status === "active" ? "Active" : "Inactive"}
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              <div>
                <p className="text-igc-cream text-sm" data-testid={`affiliate-tracker-leads-${a.id}`}>{a.leads_assigned}</p>
                <p className="text-igc-muted text-[10px]" title="The total number of leads this affiliate has ever been assigned.">Leads Assigned</p>
              </div>
              <div>
                <p className="text-igc-cream text-sm" data-testid={`affiliate-tracker-closed-${a.id}`}>{a.deals_closed}</p>
                <p className="text-igc-muted text-[10px]" title="The number of deals this affiliate has successfully closed (client became a paying client).">Deals Closed</p>
              </div>
              <div>
                <p className="text-igc-success text-sm" data-testid={`affiliate-tracker-earned-${a.id}`}>${a.commission_earned.toLocaleString()}</p>
                <p className="text-igc-muted text-[10px]" title="Total commission this affiliate has earned to date, whether or not it's been paid out yet.">Commission Earned</p>
              </div>
              <div>
                <p className="text-igc-goldText text-sm" data-testid={`affiliate-tracker-paid-${a.id}`}>${a.commission_paid.toLocaleString()}</p>
                <p className="text-igc-muted text-[10px]" title="How much of the earned commission has actually been paid out to this affiliate so far.">Commission Paid</p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Input
                data-testid={`affiliate-tracker-phone-input-${a.id}`}
                placeholder="Phone"
                title="Edit this affiliate's contact phone number, then click Save Phone."
                defaultValue={a.phone}
                onChange={(e) => setPhoneDraft({ ...phoneDraft, [a.id]: e.target.value })}
                className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream w-40 h-9 text-xs"
              />
              <Button data-testid={`affiliate-tracker-phone-save-${a.id}`} onClick={() => update(a.id, { phone: phoneDraft[a.id] ?? a.phone }, "phone")} title="Saves the phone number you just typed." className="h-9 text-xs bg-transparent border border-igc-gold text-igc-goldText hover:bg-igc-gold hover:text-black">
                Save Phone
              </Button>

              <Input
                data-testid={`affiliate-tracker-email-input-${a.id}`}
                placeholder="Contact email"
                title="Edit this affiliate's contact email address, then click Save Email."
                defaultValue={a.email}
                onChange={(e) => setEmailDraft({ ...emailDraft, [a.id]: e.target.value })}
                className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream w-48 h-9 text-xs"
              />
              <Button data-testid={`affiliate-tracker-email-save-${a.id}`} onClick={() => update(a.id, { contact_email: emailDraft[a.id] ?? a.email }, "email")} title="Saves the email address you just typed." className="h-9 text-xs bg-transparent border border-igc-gold text-igc-goldText hover:bg-igc-gold hover:text-black">
                Save Email
              </Button>

              <button
                data-testid={`affiliate-tracker-manager-toggle-${a.id}`}
                onClick={() => update(a.id, { is_manager_tier: !a.is_manager_tier }, "manager tier status")}
                title="Promotes this affiliate to Manager Tier (they'll get an override commission on their own team's deals) or removes that tier if they already have it."
                className="text-xs px-3 h-9 border border-igc-gold/30 text-igc-muted hover:text-igc-goldText"
              >
                {a.is_manager_tier ? "Remove Manager Tier" : "Promote to Manager Tier"}
              </button>

              {a.is_manager_tier && (
                <>
                  <Input
                    data-testid={`affiliate-tracker-override-input-${a.id}`}
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    placeholder="Override rate (0-1)"
                    title="How much of this manager's team's earned commission they automatically get, as a decimal (e.g. 0.10 = 10%)."
                    defaultValue={a.override_commission_rate}
                    onChange={(e) => setRateDraft({ ...rateDraft, [a.id]: e.target.value })}
                    className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream w-40 h-9 text-xs"
                  />
                  <Button data-testid={`affiliate-tracker-override-save-${a.id}`} onClick={() => update(a.id, { override_commission_rate: parseFloat(rateDraft[a.id] ?? a.override_commission_rate) || 0 }, "override rate")} title="Saves the override rate you just typed." className="h-9 text-xs bg-transparent border border-igc-gold text-igc-goldText hover:bg-igc-gold hover:text-black">
                    Save Rate
                  </Button>
                  <span className="text-igc-muted text-[10px]" data-testid={`affiliate-tracker-downline-count-${a.id}`} title="How many affiliates report up to this manager.">{a.downline_count} downline affiliate{a.downline_count === 1 ? "" : "s"}</span>
                </>
              )}

              {!a.is_manager_tier && managers.length > 0 && (
                <Select value={a.upline_affiliate_id || "none"} onValueChange={(v) => update(a.id, { upline_affiliate_id: v === "none" ? "" : v }, "upline manager assignment")}>
                  <SelectTrigger data-testid={`affiliate-tracker-upline-select-${a.id}`} title="Assigns this affiliate to report under a specific manager, so that manager earns override commission on this rep's deals." className="w-48 h-9 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream text-xs"><SelectValue placeholder="Assign upline manager" /></SelectTrigger>
                  <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                    <SelectItem value="none">No upline</SelectItem>
                    {managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.affiliate_code} — {m.display_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
